import React, { useState } from 'react';
import { SeTooltip } from './SeTooltip';

type InfoIconTooltipProps = {
  /** tooltip 文案。可能是普通字符串，也可能是 steps 类 JSON 字符串。 */
  tooltip: string | undefined | null;
  /** 图标 class，默认 variable-info-icon；upload/video chip 传 upload-info-icon。 */
  className?: string;
  /** 是否附加 hidden class（已填值时隐藏，但仍占位，保持 V1 行为）。 */
  hidden?: boolean;
  /** 透传给 <i> 的 data-variable-key（variable / markdown 系列用）。 */
  dataVariableKey?: string;
  /** 透传给 <i> 的 data-upload-key（upload / video 系列用）。 */
  dataUploadKey?: string;
};

/**
 * 判断 tooltip 是否为 steps 类 JSON（与 V1 markdown/render.ts 的 isStepsTooltipRaw 一致）。
 * steps 类有独立的「查看教程」交互，不应把原始 JSON 当作 hover 文案弹出。
 */
export const isStepsTooltipRaw = (raw: string | undefined | null): boolean => {
  const s = (raw ?? '').trim();
  if (!s || !(s.startsWith('{') && s.endsWith('}'))) return false;
  try {
    const obj: unknown = JSON.parse(s);
    if (!obj || typeof obj !== 'object') return false;
    const rec = obj as Record<string, unknown>;
    return (
      rec.type === 'steps' &&
      Array.isArray(rec.platforms) &&
      rec.platforms.length > 0
    );
  } catch {
    return false;
  }
};

/**
 * 缺口 #20：统一的 info-icon + hover tooltip 小组件。
 *
 * 背景：V2 大量节点渲染了带 data-tooltip 的裸 `<i class="...variable-info-icon">`，
 * 但没有任何控制器消费 data-tooltip → hover 全线失效。这里用 V2 干净路径
 * （受控 SeTooltip，同 UploadChip 范式）统一驱动 hover 展示。
 *
 * - 只处理「字符串文案」的 hover；steps 类 JSON 不弹原始文本（由独立教程入口承载）。
 * - 保留 V1 的 class（iconfont icon-tishi variable-info-icon / upload-info-icon）、
 *   data-tooltip、data-variable-key / data-upload-key，避免破坏既有样式/选择器/测试。
 */
export const InfoIconTooltip: React.FC<InfoIconTooltipProps> = ({
  tooltip,
  className = 'variable-info-icon',
  hidden,
  dataVariableKey,
  dataUploadKey,
}) => {
  // 气泡 open 仅由 icon 的 hover 控制 → hover 整个框不弹，只有 hover icon 才弹（对齐用户诉求）。
  const [open, setOpen] = useState(false);
  const text = tooltip ?? '';
  if (!text) return null;

  const iconEl = (
    <i
      style={{ fontWeight: 100 }}
      className={`iconfont icon-tishi ${className}${hidden ? ' hidden' : ''}`}
      data-tooltip={text}
      {...(dataVariableKey != null ? { 'data-variable-key': dataVariableKey } : {})}
      {...(dataUploadKey != null ? { 'data-upload-key': dataUploadKey } : {})}
      aria-label="提示"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    />
  );

  // steps 类 JSON 不作为 hover 文案展示，仅保留图标占位（与 V1 语义一致）。
  if (isStepsTooltipRaw(text)) return iconEl;

  // 气泡相对整框居中（对齐 tag）：放一个撑满父框的隐形 anchor，Tooltip 锚定它而非 icon，
  // 故气泡水平居中于整个 chip。anchor 用 pointer-events:none 不抢 chip 交互、不自触发。
  // 父框（.variable-chip / .se-variable / .video-inner-wrapper）均为 position:relative。
  return (
    <>
      {iconEl}
      <SeTooltip title={text} open={open}>
        <span
          className="se-info-anchor"
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
          }}
        />
      </SeTooltip>
    </>
  );
};
